import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getCheckingStatus, recordChecking } from '../actions/checkingActions'
import { toastInfo, toastError } from './swal/toast'

export default function CheckingWidget() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { connected, checking, loading } = useSelector(state => state.checking)
    const [elapsed, setElapsed] = useState('')
    const [isWorking, setIsWorking] = useState(false)

    useEffect(() => {
        dispatch(getCheckingStatus())
    }, [dispatch])

    useEffect(() => {
        setIsWorking(!!checking)
    }, [checking])

    useEffect(() => {
        if (!checking?.start?.date) {
            setElapsed('')
            return
        }

        const updateElapsed = () => {
            const start = new Date(checking.start.date)
            const now = new Date()
            const diffMs = now - start
            const totalMinutes = Math.floor(diffMs / 60000)
            const hours = Math.floor(totalMinutes / 60)
            const minutes = totalMinutes % 60
            setElapsed(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`)
        }

        updateElapsed()
        const interval = setInterval(updateElapsed, 60000)
        return () => clearInterval(interval)
    }, [checking])

    const handleRecord = useCallback(async () => {
        try {
            const result = await dispatch(recordChecking())

            if (result.action === 'wait') {
                toastInfo(result.message)
                return
            }

            dispatch(getCheckingStatus())
        } catch (error) {
            toastError(error.message || t('teacher.checking.error'))
        }
    }, [dispatch, t])

    if (!connected) return null

    return (
        <div
            className='d-flex align-items-center gap-2 px-3 py-1 rounded-3'
            style={{
                background: isWorking ? '#dcfce7' : '#f3f4f6',
                border: isWorking ? '1px solid #86efac' : '1px solid #e5e7eb',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: loading ? 0.7 : 1
            }}
            onClick={!loading ? handleRecord : undefined}
        >
            <div
                style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: isWorking ? '#22c55e' : '#9ca3af',
                    animation: isWorking ? 'pulse 2s infinite' : 'none'
                }}
            />
            <div className='d-flex flex-column'>
                <span className='fw-bold' style={{ fontSize: '13px', color: isWorking ? '#166534' : '#374151', lineHeight: 1.2 }}>
                    {isWorking ? t('teacher.checking.clockOut') : t('teacher.checking.clockIn')}
                </span>
                {isWorking && elapsed && (
                    <span style={{ fontSize: '11px', color: '#166534', lineHeight: 1.2, fontFamily: 'monospace' }}>
                        {elapsed}
                    </span>
                )}
            </div>
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                }
            `}</style>
        </div>
    )
}
